import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const animalCategories = {
    schoolItems: [
        "blackboard.png", "chalk.png", "eraser.png", "desk.png", "chair.png", "notebook.png", "pen.png", "pencil.png",
        "sharpener.png", "ruler.png", "globe.png", "projector.png", "marker.png", "highlighter.png", "scissors.png",
        "glue.png", "stapler.png", "paperclip.png", "calculator.png", "bookshelf.png", "books_image.png", "microscope.png", "test_tube.png", "beaker.png", "flask.png", "magnifying_glass.png", "telescope.png"
    ],
    otherItems: [
        "sofa.png", "bed.png", "dining_table.png", "chair.png", "wardrobe.png", "lamp.png",
        "television.png", "mirror.png", "clock.png", "swing.png", "slide.png", "seesaw.png", "tree.png", "drill.png", "ladder.png", "bulldozer.png", "crane.png", "wheelbarrow.png", "cement_mixer.png", "hard_hat.png", "bricks.png", "stethoscope.png", "syringe.png", "wheelchair.png", "ambulance.png"
    ]
};


function shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
        // Generate a random index
        const randomIndex = Math.floor(Math.random() * (i + 1));
        // Swap elements
        [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
    }
    return array;
}

// Question Type
type QuestionType = {
    text: string;
    correctImages: string[];
    allImages: string[];
};



// Dynamic Sizing
const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    const imageSize = screenWidth > 1024 ? 90 : screenWidth > 600 ? 100 : 60;
    return { imageSize, minDistance: imageSize * 1.5 };
};

const ChooseAnimalType = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: boolean }>({});
    const [sizes, setSizes] = useState(getDynamicSizes());
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [correctImage, setCorrectImage] = useState<any[]>();
    const [selectedImage, setSelectedImage] = useState('');
    const modal = useRef<HTMLIonModalElement>(null);

    // Generate a question
    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const categories = Object.keys(animalCategories) as (keyof typeof animalCategories)[];
        const randomCategory = categories[0];

        const correctPool = animalCategories[randomCategory];
        const correctImages = shuffleArray(correctPool).slice(0, 5);
        setCorrectImage(correctImages);

        const otherCategories = categories.filter(cat => cat !== randomCategory);
        const otherImagesPool = otherCategories.flatMap(cat => animalCategories[cat]);
        const uniqueArr = otherImagesPool.filter((value, index, self) => self.indexOf(value) === index).filter(item => !correctImages.includes(item));
        // Ensure enough incorrect images exist
        const incorrectImages = shuffleArray(uniqueArr).slice(0, remainingImages);
        const allImages = [...correctImages, ...incorrectImages].sort(() => 0.5 - Math.random());

        return {
            text: `Find School Items`,
            correctImages,
            allImages,
        };
    };

    // Memoized function to generate a new question
    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;
        //setShowImage(true);
        setTimeout(() => {
            const imageSize = sizes.imageSize;
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20));
            const rows = Math.floor(container.height / (imageSize + 20));
            const totalImage = columns * rows;
            const remainingImages = totalImage > 5 ? totalImage - 5 : 0; // Prevent negative values
            setShowImage(false);
            setQuestion(generateRandomQuestion(remainingImages));
        }, 1000);

    }, [sizes]);

    // Generate a question only once on mount
    useEffect(() => {
        generateQuestion();
        document.title = "My School – Choose the Correct Answer Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);



    const handleImageClick = (image: string) => {
        setSelectedImage(image);
        setSelectedAnswers(prev => {
            const newState = { ...prev };
            if (image in newState) {
                delete newState[image];
            } else {
                newState[image] = question?.correctImages.includes(image) ?? false;
            }
            return newState;
        });
    };

    // Check answer and generate new question
    useEffect(() => {
        if (!question) return;

        const selectedCorrectCount = Object.values(selectedAnswers).filter(val => val === true).length;
        const totalCorrect = question.correctImages.length;
        const allSelected = Object.keys(selectedAnswers).length === totalCorrect;

        if (selectedCorrectCount === totalCorrect && allSelected) {
            //setShowImage(true);
            setTimeout(() => {
                setSelectedAnswers({});
                setQuestion(null)
                generateQuestion();
                //setShowImage(false);
                setSelectedImage('');

            }, 2000);
        }
    }, [selectedAnswers, question, generateQuestion]);

    return (
        <>
            <Helmet>
                <meta name="description" content="Interactive worksheet for UKG kids to choose the correct answer about school-related topics. Build vocabulary, memory, and awareness of school environment." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/my-school/choose-correct" />
            </Helmet>
            {showImage ? (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} alt="Loading" />
                </div>
            ) : (
                <div className="contentPart additionWord chooseImage match-container">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>My School – Choose the Correct Answer</h1>

                            <p>
                                Let’s help young learners become familiar with their school environment! This engaging UKG worksheet focuses on <strong>Choose the Correct Answer</strong> questions to build vocabulary and awareness of common school-related places, people, and objects.
                            </p>

                            <h2>🏫 What Will Children Learn?</h2>
                            <ul>
                                <li>Identify school-related items like blackboard, bench, bag, etc.</li>
                                <li>Recognize people in school such as teachers, principal, and peon</li>
                                <li>Understand places like classroom, library, playground</li>
                                <li>Improve decision-making through multiple choice questions</li>
                            </ul>

                            <h2>✏️ Worksheet Format</h2>
                            <p>
                                Each question has a prompt with 3–4 answer options. Children must <strong>choose the correct answer</strong> by understanding the clue or visual hint.
                            </p>

                            <h2>📘 Sample Questions</h2>
                            <ul>
                                <li>Who teaches you in school?</li>
                                <li>Where do you play in school?</li>
                                <li>Which of these is used for writing?</li>
                                <li>What do you carry your books in?</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Familiarity with school objects, places, and people</li>
                                <li>Improved vocabulary and reading comprehension</li>
                                <li>Practice in answering multiple-choice questions</li>
                                <li>Boosting school readiness and general awareness</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>My School – Choose the Correct Answer</h1>

                        <p>
                            Let’s help young learners become familiar with their school environment! This engaging UKG worksheet focuses on <strong>Choose the Correct Answer</strong> questions to build vocabulary and awareness of common school-related places, people, and objects.
                        </p>

                        <h2>🏫 What Will Children Learn?</h2>
                        <ul>
                            <li>Identify school-related items like blackboard, bench, bag, etc.</li>
                            <li>Recognize people in school such as teachers, principal, and peon</li>
                            <li>Understand places like classroom, library, playground</li>
                            <li>Improve decision-making through multiple choice questions</li>
                        </ul>

                        <h2>✏️ Worksheet Format</h2>
                        <p>
                            Each question has a prompt with 3–4 answer options. Children must <strong>choose the correct answer</strong> by understanding the clue or visual hint.
                        </p>

                        <h2>📘 Sample Questions</h2>
                        <ul>
                            <li>Who teaches you in school?</li>
                            <li>Where do you play in school?</li>
                            <li>Which of these is used for writing?</li>
                            <li>What do you carry your books in?</li>
                        </ul>

                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Sense Organs</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-home"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My Home</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-school"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My School</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Familiarity with school objects, places, and people</li>
                            <li>Improved vocabulary and reading comprehension</li>
                            <li>Practice in answering multiple-choice questions</li>
                            <li>Boosting school readiness and general awareness</li>
                        </ul>
                    </article>
                    <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                    <div ref={containerRef} className="chooseImageContainer" style={{ position: "relative", width: "100%", height: "100%", justifyContent: "center", alignItems: "center", overflow: "hidden", display: "grid" }}>
                        {question?.allImages.map((img, index) => (
                            <div key={img + index} >
                                <img src={`${process.env.REACT_APP_API_URL}${img}`} alt="option" onClick={() => handleImageClick(img)} style={{
                                    width: `${sizes.imageSize}px`,
                                    height: `${sizes.imageSize}px`,
                                    cursor: "pointer",
                                    border: `5px solid ${img in selectedAnswers ? (selectedAnswers[img] === true ? "green" : "red") : "transparent"}`,
                                    transition: "transform 0.2s ease-in-out",
                                }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default ChooseAnimalType;
